import { clearCache, useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import { symptomTrackingActions } from 'store/symptomTracking';
import {
  selectDailyStatuses,
  selectIsPeriodReminderEnabled,
  selectReminderTime,
} from 'store/symptomTracking/selectors';
import useSchedulePeriodNotifications from './useSchedulePeriodNotifications';

export type CycleStats = {
  start: Dayjs;
  periodLength: number;
  totalLength: number;
  completed: boolean;
  predicted?: boolean;
};

export default function useSymptomTrackingData() {
  const { t } = useTranslation();
  const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0);
  const dispatch = useDispatch();
  const { loading: loadingData, refresh } = useRequest(() => ApiSymptomTrackingManager.getHistory(), {
    cacheKey: 'tracking.history',
    onSuccess: data => {
      dispatch(symptomTrackingActions.setDailyStatuses(data));
    },
  });

  const statusesList = useSelector(selectDailyStatuses);

  const reminderTime = useSelector(selectReminderTime);
  const areDailyNotificationsEnabled = reminderTime !== null;
  const isPeriodReminderEnabled = useSelector(selectIsPeriodReminderEnabled);
  const { schedulePeriodReminder } = useSchedulePeriodNotifications();

  const { run: fillPeriodDays } = useRequest(
    (startDate: Dayjs, numberOfDays: number, menstruationAmount?: number) => {
      const startDateMidnight = startDate.set('hour', 0).set('minute', 0).set('second', 0);
      const endDate = dayjs.min(
        startDateMidnight.add(numberOfDays - 1, 'days'),
        today.set('hour', 0).set('minute', 0).set('second', 0),
      );

      const daysList = Array.from({ length: endDate.diff(startDateMidnight, 'days') + 1 }, (_, i) =>
        startDate.add(i, 'days').format('YYYY-MM-DD'),
      );

      if (menstruationAmount !== undefined) {
        dispatch(symptomTrackingActions.setPeriodForDays({ days: daysList, menstruationAmount }));
      } else {
        dispatch(symptomTrackingActions.addPeriodToEmptyDays(daysList));
      }

      return ApiSymptomTrackingManager.fillPeriodDays(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        menstruationAmount,
      );
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const { run: fillPeriodDay } = useRequest(
    (date: Dayjs, menstruationAmount: number) => {
      const dateString = date.format('YYYY-MM-DD');
      dispatch(symptomTrackingActions.setPeriodForDays({ days: [dateString], menstruationAmount }));
      return ApiSymptomTrackingManager.fillPeriodDays(dateString, dateString, menstruationAmount);
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const { run: removePeriodFromDay } = useRequest(
    (date: Dayjs) => {
      dispatch(symptomTrackingActions.removePeriodFromDay(date.format('YYYY-MM-DD')));
      return ApiSymptomTrackingManager.removePeriodFromDay(date.format('YYYY-MM-DD'));
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const { run: removePeriodRange } = useRequest(
    (startDate: Dayjs, endDate: Dayjs) => {
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      dispatch(symptomTrackingActions.removePeriodForRange({ startDate: startDateStr, endDate: endDateStr }));
      return ApiSymptomTrackingManager.removePeriodDays(startDateStr, endDateStr);
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const loading = statusesList === undefined && loadingData;
  const hasTrackingBeenUsed = !loading && statusesList && statusesList.length > 0;

  const statusPerDay = Object.fromEntries((statusesList || []).map(status => [status.date as string, status]));

  const getSpecificDailyStatus = (date: Dayjs) => {
    return statusPerDay[date.format('YYYY-MM-DD')];
  };

  const hasTrackingForToday = getSpecificDailyStatus(today) !== undefined;

  const getDisplayedDays = (selectedDate: Dayjs) => {
    if (today.format('YYYY-MM') === selectedDate.format('YYYY-MM')) {
      // show last 31 days if we are looking at current month
      // (to avoid having empty graphs at the start of the month)
      return Array.from({ length: 31 }, (_, i) => today.subtract(i, 'days')).reverse();
    }

    return Array.from({ length: selectedDate.daysInMonth() }, (_, i) =>
      dayjs(new Date(selectedDate.year(), selectedDate.month(), i + 1)),
    );
  };

  const getStatusValueForDisplayedDays = (
    value: keyof DailyStatus,
    displayedDays: Dayjs[],
    defaultValue: any = null,
  ): any[] => {
    return displayedDays.map(day => {
      const dailyStatus = getSpecificDailyStatus(dayjs(day));
      return dailyStatus ? dailyStatus[value] : defaultValue;
    });
  };

  const getPeriodStats = useCallback(() => {
    if (!statusesList) return [];

    let isInPeriod = false;
    let lastBleedingDay: string | null = null;
    const cyclesHistory: CycleStats[] = [];
    const MAX_GAP_DAYS = 3;

    statusesList.forEach(status => {
      if (status.menstruation_amount > 0) {
        const currentDate = dayjs(status.date);

        if (!isInPeriod) {
          // Starting a new period
          if (cyclesHistory.length > 0) {
            const previousCycle = cyclesHistory[cyclesHistory.length - 1];
            previousCycle.completed = true;
            previousCycle.totalLength = currentDate.diff(dayjs(previousCycle.start), 'days');
            // Update previous cycle's period length based on its last bleeding day
            if (lastBleedingDay) {
              previousCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(previousCycle.start), 'days') + 1;
            }
          }

          cyclesHistory.push({
            start: currentDate,
            periodLength: 0, // Will be calculated when cycle ends or when we have the last bleeding day
            totalLength: 0,
            completed: false,
          });
          isInPeriod = true;
        } else if (lastBleedingDay) {
          // Check if we're still within the same period (allowing up to 3 days gap)
          const daysSinceLastBleeding = currentDate.diff(dayjs(lastBleedingDay), 'days');

          if (daysSinceLastBleeding > MAX_GAP_DAYS + 1) {
            // Gap is too large, start a new cycle
            const previousCycle = cyclesHistory[cyclesHistory.length - 1];
            previousCycle.completed = true;
            previousCycle.totalLength = currentDate.diff(dayjs(previousCycle.start), 'days');
            // Update previous cycle's period length based on its last bleeding day
            previousCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(previousCycle.start), 'days') + 1;

            cyclesHistory.push({
              start: currentDate,
              periodLength: 0, // Will be calculated when cycle ends
              totalLength: 0,
              completed: false,
            });
          }
          // If continuing the current period, we don't need to do anything here
          // The period length will be calculated based on the last bleeding day
        }

        lastBleedingDay = status.date || null;
      } else if (isInPeriod && lastBleedingDay) {
        // Check if we should end the current period
        const daysSinceLastBleeding = dayjs(status.date).diff(dayjs(lastBleedingDay), 'days');
        if (daysSinceLastBleeding > MAX_GAP_DAYS) {
          isInPeriod = false;
        }
      }
    });

    // Handle the last cycle
    if (cyclesHistory.length > 0) {
      const lastCycle = cyclesHistory[cyclesHistory.length - 1];
      lastCycle.totalLength = dayjs(today).diff(dayjs(lastCycle.start), 'days') + 1;
      // Update last cycle's period length based on its last bleeding day
      if (lastBleedingDay) {
        lastCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(lastCycle.start), 'days') + 1;
      }
    }

    return cyclesHistory;
  }, [statusesList, today]);

  const cycleHistory = useMemo(() => getPeriodStats(), [getPeriodStats]);
  const completedCycles = useMemo(() => cycleHistory.filter(cycle => cycle.completed), [cycleHistory]);

  // average cycle length
  const cycleStats = useMemo(() => {
    const average = completedCycles.reduce((acc, cycle) => acc + cycle.totalLength, 0) / completedCycles.length;
    const periodLengthAverage =
      completedCycles.reduce((acc, cycle) => acc + cycle.periodLength, 0) / completedCycles.length;
    // Calculate standard deviation
    const variance =
      completedCycles.reduce((acc, cycle) => acc + Math.pow(cycle.totalLength - average, 2), 0) /
      completedCycles.length;
    const standardDeviation = Math.sqrt(variance);
    const roundedStandardDeviation = Math.round(standardDeviation);
    // Calculate max variation from average
    const maxVariationFromAverage = completedCycles.reduce(
      (acc, cycle) => Math.max(acc, Math.abs(average - cycle.totalLength)),
      0,
    );
    return {
      average,
      periodLengthAverage,
      standardDeviation,
      roundedStandardDeviation,
      maxVariationFromAverage,
    };
  }, [completedCycles]);

  const probableNextPeriodStartDates = useMemo(() => {
    if (cycleHistory.length < 2) return null;
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    let start = lastCycle.start.add(cycleStats.average - cycleStats.standardDeviation, 'days');
    let end = lastCycle.start.add(cycleStats.average + cycleStats.standardDeviation, 'days');
    if (start.isBefore(today, 'day')) start = today;
    if (end.isBefore(today, 'day')) end = today;
    return {
      start,
      mostLikely: lastCycle.start.add(cycleStats.average, 'days'),
      end,
    };
  }, [cycleHistory, cycleStats, today]);

  const getPredictionForDay = useCallback(
    (day: Dayjs) => {
      if (cycleHistory.length < 2) return null;

      const lastCycle = cycleHistory[cycleHistory.length - 1];
      const daysSinceLastCycleStart = day.diff(lastCycle.start, 'days');

      if (daysSinceLastCycleStart < 0) return null;

      const estimatedCycleNumber = daysSinceLastCycleStart / cycleStats.average;
      const minCycleNumber = Math.floor(estimatedCycleNumber);
      const maxCycleNumber = Math.ceil(estimatedCycleNumber);

      for (let cycleNumber = minCycleNumber; cycleNumber <= maxCycleNumber; cycleNumber++) {
        if (cycleNumber === 0) continue;

        const predictedCycleStart = lastCycle.start.add(cycleStats.average * cycleNumber, 'days');

        const mostLikelyPeriodStart = predictedCycleStart;
        const mostLikelyPeriodEnd = predictedCycleStart.add(cycleStats.periodLengthAverage - 1, 'days');

        const lessLikelyPeriodStart = predictedCycleStart.subtract(cycleStats.standardDeviation, 'days');
        const lessLikelyPeriodEnd = mostLikelyPeriodEnd.add(cycleStats.standardDeviation, 'days');

        const isInLessLikelyPeriod =
          day.isSameOrAfter(lessLikelyPeriodStart, 'day') && day.isSameOrBefore(lessLikelyPeriodEnd, 'day');

        if (isInLessLikelyPeriod) {
          const isInMostLikelyPeriod =
            day.isSameOrAfter(mostLikelyPeriodStart, 'day') && day.isSameOrBefore(mostLikelyPeriodEnd, 'day');

          return {
            cycleNumber,
            isInMostLikelyPeriod,
            isInLessLikelyPeriod,
          };
        }
      }

      return null;
    },
    [cycleHistory, cycleStats],
  );

  useEffect(() => {
    if (isPeriodReminderEnabled && probableNextPeriodStartDates) {
      // re-schedule period reminders in case the user adds a new period,
      // removes a period, updates a period or does anything that could affect the prediction
      schedulePeriodReminder(probableNextPeriodStartDates.start, areDailyNotificationsEnabled);
    }
  }, [isPeriodReminderEnabled, probableNextPeriodStartDates, areDailyNotificationsEnabled]);

  const daysUntilNextPeriod = useMemo(() => {
    if (!probableNextPeriodStartDates) return null;
    const min = probableNextPeriodStartDates.start.diff(today, 'days');
    const max = probableNextPeriodStartDates.end.diff(today, 'days');
    return { min, mostLikely: probableNextPeriodStartDates.mostLikely.diff(today, 'days'), max };
  }, [probableNextPeriodStartDates, today]);

  const probableNextPeriodDates = useMemo(() => {
    if (!probableNextPeriodStartDates) return null;
    return {
      start: probableNextPeriodStartDates.start,
      end: probableNextPeriodStartDates.end.add(cycleStats.periodLengthAverage, 'days'),
    };
  }, [probableNextPeriodStartDates, cycleStats]);

  const isWithinPeriod = useMemo(() => {
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    if (!lastCycle) return false;

    return today.isSameOrBefore(lastCycle.start.add(lastCycle.periodLength, 'days'), 'day');
  }, [cycleHistory, today]);

  const probableDaysUntilEndOfPeriod = useMemo(() => {
    if (!isWithinPeriod) return null;
    if (!cycleStats.periodLengthAverage) return null;
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    if (!lastCycle) return null;
    return Math.max(0, lastCycle.start.add(cycleStats.periodLengthAverage, 'days').diff(today, 'day'));
  }, [cycleHistory, today, cycleStats.periodLengthAverage, isWithinPeriod]);

  const nextPeriodPrediction = useMemo(() => {
    if (daysUntilNextPeriod) {
      if (daysUntilNextPeriod.mostLikely > 5) {
        // next period is more than 5 days away
        return {
          predictionLabel: t('symptomTracking.nextPeriod.nextPeriodAround'),
          predictionValue: probableNextPeriodStartDates?.mostLikely
            .format('LL')
            .replace(dayjs().year().toString(), '')
            .trim(),
        };
      } else {
        // next period is less than 5 days away
        return {
          predictionLabel: t('symptomTracking.nextPeriod.nextPeriod'),
          predictionValue:
            daysUntilNextPeriod.mostLikely <= 0
              ? t('common.today')
              : daysUntilNextPeriod.mostLikely === 1
                ? t('common.tomorrow')
                : probableNextPeriodStartDates?.mostLikely.format('dddd'),
        };
      }
    }
    // no prediction available
    return {
      predictionLabel: t('symptomTracking.nextPeriod.nextPeriodIn'),
      predictionValue: (
        <>
          <span className="text-subtle-foreground">-</span> {t('symptomTracking.nextPeriod.days')}
        </>
      ),
    };
  }, [daysUntilNextPeriod, probableNextPeriodStartDates, t]);

  const cycleDaysWithCenteredPeriod = useMemo(() => {
    const midpoints: Dayjs[] = [];

    // the first midpoint is the start of the first cycle
    if (cycleHistory.length > 0) midpoints.push(cycleHistory[0].start);

    // for all cycles except the last one, add the midpoint of the cycle to the list
    for (let i = 0; i < cycleHistory.length - 1; i += 1) {
      // for all cycles except the last one, add the midpoint of the cycle to the list
      const cycle = cycleHistory[i];
      const midpoint = dayjs(cycle.start).add(cycle.totalLength / 2, 'days');
      midpoints.push(midpoint);
    }

    // for the last cycle, it's a bit more complicated
    if (cycleHistory.length > 0) {
      const lastCycle = cycleHistory[cycleHistory.length - 1];
      const lastCycleDay = lastCycle.start.add(lastCycle.totalLength, 'days');
      const predictedMidpoint = probableNextPeriodStartDates
        ? lastCycle.start.add(probableNextPeriodStartDates.start.diff(lastCycle.start, 'days') / 2, 'days')
        : null;
      if (predictedMidpoint === null || lastCycleDay.isSameOrBefore(predictedMidpoint, 'day')) {
        // no prediction or before the prediction: add the last day of the current cycle
        midpoints.push(lastCycleDay);
      } else if (probableNextPeriodStartDates) {
        // we have a prediction and the last day of the current cycle is after the prediction:
        // then we create a new "predicted" cycle
        midpoints.push(
          lastCycle.start.add(probableNextPeriodStartDates.start.diff(lastCycle.start, 'days') / 2, 'days'),
        );
        midpoints.push(dayjs.max(probableNextPeriodStartDates.start, lastCycleDay));
      }
    }

    const daysPerCycle = [];

    for (let m = 0; m < midpoints.length - 1; m += 1) {
      const days = Array.from({ length: midpoints[m + 1].diff(midpoints[m], 'days') }, (_, j) =>
        midpoints[m].add(j, 'days'),
      );
      if (m < cycleHistory.length) {
        daysPerCycle.push({ days, cycle: cycleHistory[m] });
      } else {
        daysPerCycle.push({
          days,
          cycle: {
            start: probableNextPeriodStartDates?.start,
            periodLength: cycleStats.periodLengthAverage,
            totalLength: cycleStats.average,
            completed: false,
            predicted: true,
          },
        });
      }
    }
    return daysPerCycle;
  }, [cycleHistory, cycleStats, probableNextPeriodStartDates]);

  const doesStatusHaveDataOtherThanPeriod = (status: DailyStatus) => {
    return (
      status.general_state !== null ||
      status.pain_level !== null ||
      status.symptoms.length > 0 ||
      status.extra_symptoms ||
      status.comment
    );
  };

  const dataOtherThanPeriod = useMemo(() => {
    const daysWithData = statusesList?.filter(doesStatusHaveDataOtherThanPeriod);
    const firstDayWithData = daysWithData?.[0];
    return {
      firstDayWithData,
      daysWithData,
    };
  }, [statusesList]);

  const hasDataOtherThanPeriod = useMemo(() => {
    return dataOtherThanPeriod.firstDayWithData !== undefined;
  }, [dataOtherThanPeriod]);

  const getFertilityStatusForDay = useCallback(
    (date: Dayjs): { type: 'ovulation' | 'fertile'; hollow: boolean } | null => {
      if (!cycleHistory.length || !isFinite(cycleStats.average)) return null;
      for (const cycle of cycleHistory) {
        const { ovulationIdx, fertileStartIdx, fertileEndIdx, isFertilityWindowPredictionReliable } =
          getOvulationAndFertilityWindow(cycle, cycleStats);
        if (!isFertilityWindowPredictionReliable) continue;
        const dayIdx = date.diff(cycle.start, 'day');
        const cycleEndIdx = cycle.completed ? cycle.totalLength - 1 : Infinity;
        if (dayIdx < 0 || dayIdx > cycleEndIdx) continue;
        const hollow = !cycle.completed;
        if (dayIdx === ovulationIdx) return { type: 'ovulation', hollow };
        if (fertileStartIdx !== null && fertileEndIdx !== null && dayIdx >= fertileStartIdx && dayIdx <= fertileEndIdx)
          return { type: 'fertile', hollow };
      }
      return null;
    },
    [cycleHistory, cycleStats],
  );

  return {
    getSpecificDailyStatus,
    getDisplayedDays,
    getStatusValueForDisplayedDays,
    fillPeriodDays,
    fillPeriodDay,
    removePeriodFromDay,
    removePeriodRange,
    refresh,
    hasTrackingBeenUsed,
    hasTrackingForToday,
    loading,
    cycleHistory,
    cycleStats,
    cycleDaysWithCenteredPeriod,
    probableNextPeriodStartDates,
    probableNextPeriodDates,
    daysUntilNextPeriod,
    nextPeriodPrediction,
    isWithinPeriod,
    probableDaysUntilEndOfPeriod,
    dataOtherThanPeriod,
    hasDataOtherThanPeriod,
    getPredictionForDay,
    getFertilityStatusForDay,
  };
}

// Utility to compute ovulation and fertility window for a given cycle
export function getOvulationAndFertilityWindow(
  cycle: CycleStats,
  cycleStats: { average: number; standardDeviation: number; maxVariationFromAverage: number },
) {
  // For a cycle in progress, the next period can start today at the earliest, so the cycle is at
  // least as long as the days elapsed before today: once it outlasts the average, the predicted
  // window slides along with it instead of staying stuck at average - 14.
  const cycleLength = cycle.completed
    ? cycle.totalLength
    : Math.max(Math.round(cycleStats.average), Math.round(cycle.totalLength) - 1);
  const startDate = cycle.start;
  const ovulationIdx = cycleLength - 14;
  const fertileStartIdx = ovulationIdx - 7;
  const fertileEndIdx = ovulationIdx + 2;

  return {
    ovulationIdx,
    fertileStartIdx,
    fertileEndIdx,
    ovulationDate: startDate.add(ovulationIdx, 'day'),
    fertileStartDate: startDate.add(fertileStartIdx, 'day'),
    fertileEndDate: startDate.add(fertileEndIdx, 'day'),
    isFertilityWindowPredictionReliable: cycleStats.average < 21 || cycleStats.standardDeviation <= 4,
  };
}
